import React, { useState, useEffect, useRef } from 'react';
import { 
  Users, Plus, Edit, Trash2, UserPlus, X, Shield, 
  Loader2, AlertCircle
} from 'lucide-react';
import { api } from './utils/api';
import { useGroupContext } from './contexts/GroupContext';
import { useToast } from './hooks/use-toast';
import { LoadingSpinner } from './components/common/Loading';

// Create Group Modal
const CreateGroupModal = ({ onClose, onCreate }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    setLoading(true);
    try {
      await api.post('/groups', { name, description });
      onCreate();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to create group';
      if (err.response?.status === 409) {
        toast({ title: 'Error', description: 'A group with this name already exists', variant: 'error' });
      } else {
        toast({ title: 'Error', description: detail, variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="medieval-border rounded-lg p-6 max-w-md w-full animate-modal-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="warcraft-title text-2xl flex items-center gap-2">
            <Plus className="w-6 h-6" /> Create Group
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">Group Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., raid-team-alpha"
              required
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Group description..."
              className="w-full"
              rows="3"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="gray-button flex-1 py-2 rounded">
              Cancel
            </button>
            <button type="submit" disabled={loading || !name.trim()} className="gold-button flex-1 py-2 rounded">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Create Group'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Group Modal
const EditGroupModal = ({ group, onClose, onSave }) => {
  const [name, setName] = useState(group?.name || '');
  const [description, setDescription] = useState(group?.description || '');
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!group) return;

    setLoading(true);
    try {
      await api.put(`/groups/${group.id}`, { name, description });
      onSave();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to update group';
      toast({ title: 'Error', description: detail, variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="medieval-border rounded-lg p-6 max-w-md w-full animate-modal-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="warcraft-title text-2xl flex items-center gap-2">
            <Edit className="w-6 h-6" /> Edit Group
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">Group Name</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full"
            />
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">Description</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full"
              rows="3"
            />
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="gray-button flex-1 py-2 rounded">
              Cancel
            </button>
            <button type="submit" disabled={loading || !name.trim()} className="gold-button flex-1 py-2 rounded">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Delete Group Confirm Modal
const DeleteGroupModal = ({ group, onClose, onDelete }) => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/groups/${group.id}`);
      onDelete();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to delete group';
      toast({ title: 'Error', description: detail, variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="medieval-border rounded-lg p-6 max-w-md w-full animate-modal-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="warcraft-title text-2xl text-red-400 flex items-center gap-2">
            <Trash2 className="w-6 h-6" /> Delete Group
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="bg-red-900/20 border border-red-700/50 p-4 rounded mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-300 font-semibold mb-1">Warning</p>
              <p className="text-sm text-red-200/80">
                This will delete the group "{group.name}" and stop all servers assigned to this group. 
                This action cannot be undone.
              </p>
            </div>
          </div>
        </div>

        <div className="flex gap-3">
          <button type="button" onClick={onClose} className="gray-button flex-1 py-2 rounded">
            Cancel
          </button>
          <button 
            onClick={handleDelete} 
            disabled={loading}
            className="red-button flex-1 py-2 rounded"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Yes, Delete Group'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Add Member Modal (with username autocomplete)
const AddMemberModal = ({ group, existingMembers = [], onClose, onAdd }) => {
  const [username, setUsername] = useState('');
  const [role, setRole] = useState('player');
  const [loading, setLoading] = useState(false);
  const [candidates, setCandidates] = useState(null); // null = not loaded yet
  const [showDropdown, setShowDropdown] = useState(false);
  const [highlighted, setHighlighted] = useState(0);
  const inputRef = useRef(null);
  const selectingRef = useRef(false);
  const { toast } = useToast();

  useEffect(() => {
    if (candidates !== null) return;
    api.get(`/groups/${group.id}/candidate-users`)
      .then((res) => setCandidates(res.data))
      .catch(() => setCandidates([])); // if the call fails, fall back to free text
  }, [group.id, candidates]);

  // Hide users who are already in this group's roster.
  const existing = new Set(existingMembers);

  const query = username.trim().toLowerCase();
  const suggestions = (candidates || [])
    .filter((u) => !existing.has(u.username))
    .filter((u) =>
      !query
        ? true
        : u.username.toLowerCase().includes(query) ||
          u.email.toLowerCase().includes(query)
    )
    .slice(0, 8);

  const selectUser = (u) => {
    selectingRef.current = true;
    setUsername(u.username);
    setShowDropdown(false);
    setTimeout(() => { selectingRef.current = false; }, 200);
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) {
      if (e.key === 'Enter' && !username.trim()) e.preventDefault();
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlighted((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      selectUser(suggestions[highlighted]);
    } else if (e.key === 'Escape') {
      setShowDropdown(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username.trim()) return;

    setLoading(true);
    try {
      await api.post(`/groups/${group.id}/members`, { username: username.trim(), role });
      onAdd();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to add member';
      if (err.response?.status === 404) {
        toast({ title: 'Error', description: 'User not found', variant: 'error' });
      } else if (err.response?.status === 409) {
        toast({ title: 'Error', description: 'User is already a member', variant: 'error' });
      } else if (err.response?.status === 400) {
        toast({ title: 'Error', description: detail, variant: 'error' });
      } else {
        toast({ title: 'Error', description: detail, variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="medieval-border rounded-lg p-6 max-w-md w-full animate-modal-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="warcraft-title text-2xl flex items-center gap-2">
            <UserPlus className="w-6 h-6" /> Add Member
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <label className="block text-sm text-gray-300 mb-2">Username</label>
            <input
              ref={inputRef}
              type="text"
              value={username}
              onChange={(e) => {
                setUsername(e.target.value);
                setShowDropdown(true);
                setHighlighted(0);
              }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => {
                if (selectingRef.current) return;
                setTimeout(() => setShowDropdown(false), 150);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Search users…"
              required
              autoComplete="off"
              className="w-full"
              data-testid="add-member-username"
            />
            {showDropdown && suggestions.length > 0 && (
              <ul
                className="absolute z-10 mt-1 w-full max-h-56 overflow-y-auto rounded border border-gray-700 bg-slate-900 shadow-lg"
                role="listbox"
                data-testid="add-member-suggestions"
              >
                {suggestions.map((u, i) => (
                  <li key={u.username} role="option" aria-selected={i === highlighted}>
                    <button
                      type="button"
                      onMouseDown={(e) => { e.preventDefault(); selectUser(u); }}
                      className={`w-full text-left px-3 py-2 flex items-center gap-2 ${
                        i === highlighted ? 'bg-slate-700' : 'hover:bg-slate-800'
                      }`}
                    >
                      <span className="w-6 h-6 rounded-full bg-slate-800 flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {u.username.charAt(0).toUpperCase()}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium truncate">{u.username}</span>
                        <span className="block text-xs text-gray-400 truncate">{u.email}</span>
                      </span>
                      <span className="text-[10px] text-gray-500">{u.role}</span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div>
            <label className="block text-sm text-gray-300 mb-2">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full"
            >
              <option value="player">Player</option>
              <option value="group-admin">Group Admin</option>
            </select>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="gray-button flex-1 py-2 rounded">
              Cancel
            </button>
            <button type="submit" disabled={loading || !username.trim()} className="gold-button flex-1 py-2 rounded">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Member'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Member Role Modal
const EditMemberRoleModal = ({ member, group, onClose, onSave }) => {
  const [role, setRole] = useState(member.role);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    try {
      await api.put(`/groups/${group.id}/members/${member.user_id}`, { role });
      onSave();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to update role';
      if (err.response?.status === 400) {
        toast({ title: 'Error', description: detail, variant: 'error' });
      } else {
        toast({ title: 'Error', description: detail, variant: 'error' });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal-backdrop flex items-center justify-center p-4" onClick={onClose}>
      <div 
        className="medieval-border rounded-lg p-6 max-w-md w-full animate-modal-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h2 className="warcraft-title text-2xl flex items-center gap-2">
            <Shield className="w-6 h-6" /> Edit Role
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="mb-4">
          <p className="text-sm text-gray-300">
            Changing role for <strong className="text-sky-400">{member.username}</strong>
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-2">New Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="w-full"
            >
              <option value="player">Player</option>
              <option value="group-admin">Group Admin</option>
            </select>
          </div>
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="gray-button flex-1 py-2 rounded">
              Cancel
            </button>
            <button type="submit" disabled={loading} className="gold-button flex-1 py-2 rounded">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Update Role'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Member List Component
const MemberList = ({ group, isGroupAdmin, isPlatformAdmin }) => {
  const { user } = useGroupContext();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingMember, setEditingMember] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    loadMembers();
  }, [group]);

  const loadMembers = async () => {
    if (!group) return;
    setLoading(true);
    try {
      const response = await api.get(`/groups/${group.id}/members`);
      setMembers(response.data);
    } catch (err) {
      console.error('Failed to load members:', err);
      toast({ title: 'Error', description: 'Failed to load members', variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = () => {
    setShowAddModal(false);
    loadMembers();
  };

  const handleUpdateRole = () => {
    setEditingMember(null);
    loadMembers();
  };

  const handleDeleteMember = async () => {
    if (!showDeleteConfirm) return;
    
    try {
      await api.delete(`/groups/${group.id}/members/${showDeleteConfirm.user_id}`);
      loadMembers();
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to remove member';
      if (err.response?.status === 400) {
        toast({ title: 'Error', description: detail, variant: 'error' });
      } else {
        toast({ title: 'Error', description: detail, variant: 'error' });
      }
    }
    setShowDeleteConfirm(null);
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'group-admin': return 'bg-purple-900/50 text-purple-400 border-purple-500';
      case 'admin': return 'bg-red-900/50 text-red-400 border-red-500';
      default: return 'bg-gray-900/50 text-gray-400 border-gray-500';
    }
  };

  if (loading) {
    return <LoadingSpinner size="lg" />;
  }

  return (
    <div className="space-y-4">
      {isGroupAdmin && (
        <button
          onClick={() => setShowAddModal(true)}
          className="gold-button px-4 py-2 rounded flex items-center gap-2"
        >
          <UserPlus className="w-4 h-4" /> Add Member
        </button>
      )}

      {members.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
          <p>No members yet</p>
        </div>
      ) : (
        <div className="space-y-2">
          {members.map((member) => (
            <div 
              key={member.user_id} 
              className="flex items-center justify-between p-3 rounded bg-black/20"
            >
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-slate-800 flex items-center justify-center text-sm font-bold">
                  {member.username.charAt(0).toUpperCase()}
                </div>
                <div>
                  <p className="font-medium">{member.username}</p>
                  <p className="text-xs text-gray-400">{member.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <span className={`text-xs px-2 py-1 rounded border ${getRoleBadge(member.role)}`}>
                  {member.role}
                </span>
                <span className="text-xs text-gray-500">
                  {new Date(member.joined_at).toLocaleDateString()}
                </span>
                {/* Can manage a member row: not yourself, and (platform admin
                    OR not trying to edit/remove a group-admin row — the backend
                    blocks non-platform-admins from removing the last
                    group-admin). */}
                {isGroupAdmin && member.user_id !== user.id &&
                  (isPlatformAdmin || member.role !== 'group-admin') && (
                  <div className="flex gap-1">
                    <button
                      onClick={() => setEditingMember(member)}
                      className="text-sky-400 hover:text-sky-300 p-1 hover:bg-sky-900/30 rounded transition-colors"
                      title="Edit role"
                    >
                      <Edit className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirm(member)}
                      className="text-red-400 hover:text-red-300 p-1 hover:bg-red-900/30 rounded transition-colors"
                      title="Remove"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showAddModal && (
        <AddMemberModal 
          group={group}
          existingMembers={members.map((m) => m.username)}
          onClose={() => setShowAddModal(false)} 
          onAdd={handleAddMember} 
        />
      )}

      {editingMember && (
        <EditMemberRoleModal 
          member={editingMember}
          group={group}
          onClose={() => setEditingMember(null)}
          onSave={handleUpdateRole}
        />
      )}

      {showDeleteConfirm && (
        <div className="modal-backdrop flex items-center justify-center p-4" onClick={() => setShowDeleteConfirm(null)}>
          <div 
            className="medieval-border rounded-lg p-6 max-w-md w-full animate-modal-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="warcraft-title text-2xl text-red-400 flex items-center gap-2">
                <Trash2 className="w-6 h-6" /> Remove Member
              </h2>
              <button onClick={() => setShowDeleteConfirm(null)} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-300">
                Remove <strong className="text-sky-400">{showDeleteConfirm.username}</strong> from the group?
              </p>
            </div>

            <div className="flex gap-3">
              <button 
                type="button" 
                onClick={() => setShowDeleteConfirm(null)}
                className="gray-button flex-1 py-2 rounded"
              >
                Cancel
              </button>
              <button 
                onClick={handleDeleteMember}
                className="red-button flex-1 py-2 rounded"
              >
                Yes, Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Manage Members Modal
const ManageMembersModal = ({ group, onClose }) => {
  const { myRoleFor } = useGroupContext();
  const canManage = myRoleFor(group) === 'group-admin' || myRoleFor(group) === 'admin';

  return (
    <div className="modal-backdrop flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="medieval-border rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-y-auto animate-modal-in"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-4">
          <h2 className="warcraft-title text-xl flex items-center gap-2">
            <Users className="w-5 h-5" /> Members — {group.name}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-6 h-6" />
          </button>
        </div>
        <MemberList
          group={group}
          isGroupAdmin={canManage}
          isPlatformAdmin={myRoleFor(group) === 'admin'}
        />
      </div>
    </div>
  );
};

// Group Card Component
const GroupCard = ({ group, isAdmin, onEdit, onDelete }) => {
  const { myRoleFor } = useGroupContext();
  const myRole = myRoleFor(group);
  const canManageMembers = isAdmin || myRole === 'group-admin' || myRole === 'admin';
  const [showMembersModal, setShowMembersModal] = useState(false);

  return (
    <div className="stone-texture rounded-lg p-6 animate-fade-in">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="warcraft-subtitle text-xl">{group.name}</h3>
          <p className="text-sm text-gray-400 mt-1">{group.description || 'No description'}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`text-xs px-2 py-1 rounded border ${myRole === 'admin' ? 'bg-red-900/50 text-red-400 border-red-500' : myRole === 'group-admin' ? 'bg-purple-900/50 text-purple-400 border-purple-500' : 'bg-gray-900/50 text-gray-400 border-gray-500'}`}>
            {myRole === 'admin' ? 'Platform Admin' : (myRole || 'Member')}
          </span>
        </div>
      </div>

      {/* Actions (platform admin: group mgmt; group-admin: member roster) */}
      {(isAdmin || canManageMembers) && (
        <div className="flex flex-wrap gap-2 mb-2">
          {isAdmin && (
            <>
              <button
                onClick={() => onEdit(group)}
                className="gray-button px-3 py-1.5 rounded text-sm flex items-center gap-1"
              >
                <Edit className="w-3 h-3" /> Edit Group
              </button>
              <button
                onClick={() => onDelete(group)}
                className="gray-button px-3 py-1.5 rounded text-sm flex items-center gap-1 text-red-400"
              >
                <Trash2 className="w-3 h-3" /> Delete Group
              </button>
            </>
          )}
          {canManageMembers && (
            <button
              onClick={() => setShowMembersModal(true)}
              className="gray-button px-3 py-1.5 rounded text-sm flex items-center gap-1"
            >
              <Users className="w-3 h-3" /> Manage Members
            </button>
          )}
        </div>
      )}

      {showMembersModal && (
        <ManageMembersModal group={group} onClose={() => setShowMembersModal(false)} />
      )}
    </div>
  );
};

// Main GroupsPage Component
export const GroupsPage = () => {
  const { groups, isPlatformAdmin, loadGroups } = useGroupContext();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [deletingGroup, setDeletingGroup] = useState(null);

  const handleCreateGroup = () => {
    setShowCreateModal(false);
    loadGroups();
  };

  const handleEditGroup = () => {
    setEditingGroup(null);
    loadGroups();
  };

  const handleDeleteGroup = () => {
    setDeletingGroup(null);
    loadGroups();
  };

  // Filter groups based on user role
  const visibleGroups = isPlatformAdmin ? groups : groups.filter(g => g.my_role !== null);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="warcraft-subtitle text-2xl flex items-center gap-2">
          <Users className="w-6 h-6" /> Friend Groups
        </h2>
        
        {isPlatformAdmin && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="gold-button px-4 py-2 rounded flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Create Group
          </button>
        )}
      </div>

      {visibleGroups.length === 0 ? (
        <div className="text-center py-12 stone-texture rounded-lg">
          <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
          <h3 className="warcraft-subtitle text-xl mb-2">No Groups Found</h3>
          <p className="text-gray-400 mb-4">
            {isPlatformAdmin 
              ? 'Create a group to get started.' 
              : 'You are not a member of any groups yet.'}
          </p>
          {isPlatformAdmin && (
            <button
              onClick={() => setShowCreateModal(true)}
              className="gold-button px-6 py-2 rounded"
            >
              Create Your First Group
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {visibleGroups.map((group) => (
            <GroupCard
              key={group.id}
              group={group}
              isAdmin={isPlatformAdmin}
              onEdit={setEditingGroup}
              onDelete={setDeletingGroup}
            />
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateGroupModal 
          onClose={() => setShowCreateModal(false)} 
          onCreate={handleCreateGroup} 
        />
      )}

      {editingGroup && (
        <EditGroupModal 
          group={editingGroup} 
          onClose={() => setEditingGroup(null)} 
          onSave={handleEditGroup} 
        />
      )}

      {deletingGroup && (
        <DeleteGroupModal 
          group={deletingGroup} 
          onClose={() => setDeletingGroup(null)} 
          onDelete={handleDeleteGroup} 
        />
      )}
    </div>
  );
};

export default GroupsPage;
